import type { OdometerEntry } from "../api/types";

export interface OdometerBounds {
  previous: OdometerEntry | null;
  next: OdometerEntry | null;
}

export function parseMileage(raw: string): number | null {
  const compact = raw.trim().replace(/\s+/g, "").replace(/km$/i, "");
  if (!compact) return null;
  if (!/^\d+(?:[.,]\d+)*$/.test(compact)) return null;

  const lastDot = compact.lastIndexOf(".");
  const lastComma = compact.lastIndexOf(",");
  let normalized = compact;

  if (lastDot !== -1 && lastComma !== -1) {
    const decimalSeparator = lastDot > lastComma ? "." : ",";
    const thousandsSeparator = decimalSeparator === "." ? "," : ".";
    normalized = compact
      .replaceAll(thousandsSeparator, "")
      .replace(decimalSeparator, ".");
  } else if (lastDot !== -1 || lastComma !== -1) {
    const separator = lastDot !== -1 ? "." : ",";
    const parts = compact.split(separator);
    const looksGrouped =
      parts.length > 1 &&
      parts[0].length <= 3 &&
      parts.slice(1).every((part) => part.length === 3);
    normalized = looksGrouped
      ? parts.join("")
      : compact.replace(separator, ".");
  }

  const value = Number(normalized);
  return Number.isFinite(value) ? value : null;
}

export function formatMileage(value: number | null | undefined): string {
  if (value === null || value === undefined) return "";
  const fixed = value.toFixed(Number.isInteger(value) ? 0 : 1);
  return fixed.includes(".") ? fixed.replace(/\.?0+$/, "") : fixed;
}

export function timestamp(value: string): number | null {
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function odometerBounds(
  entries: OdometerEntry[],
  entryDate: string,
): OdometerBounds {
  const selected = timestamp(entryDate);
  if (selected === null) return { previous: null, next: null };

  const sorted = [...entries].sort((a, b) => {
    const byDate = (timestamp(a.datetime) ?? 0) - (timestamp(b.datetime) ?? 0);
    return byDate || a.id - b.id;
  });
  let previous: OdometerEntry | null = null;
  let next: OdometerEntry | null = null;

  for (const entry of sorted) {
    const entryTs = timestamp(entry.datetime);
    if (entryTs === null) continue;
    if (entryTs <= selected) previous = entry;
    else {
      next = entry;
      break;
    }
  }

  return { previous, next };
}
